<template>
    <div>
        <!-- Header with status selector and delete -->
        <v-card outlined class="mb-4">
            <v-card-text>
                <div class="d-flex align-center justify-space-between mb-2">
                    <h2 class="text-h5 font-weight-bold">{{ request.job_title }}</h2>
                    <div class="d-flex align-center" style="gap: 8px;">
                        <v-select
                            :value="request.status"
                            :items="jobStatuses"
                            dense
                            outlined
                            hide-details
                            style="max-width: 220px;"
                            @change="updateField('status', $event)"
                        />
                        <v-btn icon color="error" @click="confirmDelete = true">
                            <v-icon>mdi-delete</v-icon>
                        </v-btn>
                    </div>
                </div>
                <p v-if="request.reason_for_hire" class="text-body-2 grey--text text--darken-1 mb-3">{{ request.reason_for_hire }}</p>

                <!-- Status Stepper -->
                <div class="status-stepper d-flex align-center flex-wrap" style="gap: 0;">
                    <div v-for="(status, idx) in jobStatuses" :key="status"
                        class="d-flex align-center"
                    >
                        <div class="text-center" style="min-width: 70px;">
                            <v-avatar
                                :size="28"
                                :color="getStepColor(status, idx)"
                                class="mb-1"
                            >
                                <v-icon v-if="isStepCompleted(status, idx)" x-small dark>mdi-check</v-icon>
                                <span v-else class="white--text" style="font-size: 10px;">{{ idx + 1 }}</span>
                            </v-avatar>
                            <div class="text-caption" style="font-size: 9px; line-height: 1.2; max-width: 75px; margin: 0 auto;">
                                {{ status }}
                            </div>
                        </div>
                        <div v-if="idx < jobStatuses.length - 1"
                            :style="{
                                width: '20px',
                                height: '2px',
                                background: isStepCompleted(status, idx) ? '#4CAF50' : '#E0E0E0',
                                marginBottom: '20px'
                            }"
                        />
                    </div>
                </div>
            </v-card-text>
        </v-card>

        <!-- Request Details (editable) -->
        <v-card outlined class="mb-4">
            <v-card-title class="text-subtitle-1 font-weight-bold">
                Request Details
                <span class="text-caption grey--text ml-2">(click any field to edit)</span>
            </v-card-title>
            <v-card-text>
                <v-row dense>
                    <v-col cols="12" sm="6" md="3">
                        <EditableField label="DEPARTMENT" :value="request.department || ''" placeholder="Not set" @save="updateField('department', $event)" />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <EditableField label="PORTFOLIO" :value="request.portfolio || ''" placeholder="Not set" @save="updateField('portfolio', $event)" />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <EditableField label="EMPLOYMENT TYPE" :value="request.employment_type || ''" placeholder="Not set" @save="updateField('employment_type', $event)" />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <EditableField label="PRIORITY" :value="request.priority || ''" placeholder="Not set" @save="updateField('priority', $event)" />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <EditableField label="EXPERIENCE" :value="request.experience || ''" placeholder="Not set" @save="updateField('experience', $event)" />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <EditableField label="SALARY RANGE" :value="request.salary_currency ? (request.salary_currency + ' ' + (request.salary_range || '')) : (request.salary_range || '')" placeholder="Not set" @save="updateField('salary_range', $event)" />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <EditableField label="MANAGER" :value="request.hiring_manager_name || ''" placeholder="Not set" @save="updateField('hiring_manager_name', $event)" />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <EditableField label="REQUESTER EMAIL" :value="request.requester_email || ''" placeholder="Not set" @save="updateField('requester_email', $event)" />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <EditableField label="CONTACT" :value="request.contact_person || ''" placeholder="Not set" @save="updateField('contact_person', $event)" />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <EditableField label="NATIONALITY PREF." :value="request.nationality_preference || ''" placeholder="Not set" @save="updateField('nationality_preference', $event)" />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <EditableField label="INTERVIEW ROUNDS" :value="String(request.interview_rounds || '')" placeholder="Not set" @save="updateField('interview_rounds', $event)" />
                    </v-col>
                </v-row>

                <div class="mt-4">
                    <EditableField label="JOB DESCRIPTION" :value="request.job_description || ''" placeholder="Click to add job description" multiline @save="updateField('job_description', $event)" />
                </div>
                <div class="mt-3">
                    <EditableField label="MANDATORY SKILLS" :value="request.mandatory_skills || ''" placeholder="Click to add mandatory skills" multiline @save="updateField('mandatory_skills', $event)" />
                </div>
                <div class="mt-3">
                    <EditableField label="OTHER MANDATORY SKILLS" :value="request.other_mandatory_skills || ''" placeholder="Click to add other mandatory skills" @save="updateField('other_mandatory_skills', $event)" />
                </div>
                <div class="mt-3">
                    <EditableField label="PREFERRED QUALIFICATIONS" :value="request.preferred_qualifications || ''" placeholder="Click to add preferred qualifications" multiline @save="updateField('preferred_qualifications', $event)" />
                </div>
                <div class="mt-3">
                    <EditableField label="REASON FOR HIRE" :value="request.reason_for_hire || ''" placeholder="Click to add reason for hire" multiline @save="updateField('reason_for_hire', $event)" />
                </div>
                <div class="mt-3">
                    <EditableField label="SUCCESS IN FIRST 90 DAYS" :value="request.success_criteria || ''" placeholder="Click to add success criteria" multiline @save="updateField('success_criteria', $event)" />
                </div>
                <div class="mt-3">
                    <EditableField label="INTERVIEW PANEL" :value="request.interview_panel || ''" placeholder="Click to add interview panel" multiline @save="updateField('interview_panel', $event)" />
                </div>
                <div class="mt-3">
                    <EditableField label="OTHER INFORMATION" :value="request.interview_notes || ''" placeholder="Click to add other information" multiline @save="updateField('interview_notes', $event)" />
                </div>
                <div class="mt-3" v-if="request.requires_dean_approval">
                    <v-chip small color="warning" dark>
                        <v-icon left small>mdi-shield-check</v-icon> Requires Dean Approval
                    </v-chip>
                </div>

                <!-- Candidate Attribute Priorities -->
                <div class="mt-4">
                    <span class="text-overline grey--text">CANDIDATE ATTRIBUTE PRIORITIES</span>
                    <div v-if="editingAttributes" class="mt-2">
                        <div v-for="attr in attributeLabels" :key="attr.key" class="d-flex align-center mb-1" style="gap: 12px;">
                            <span class="text-caption" style="min-width: 140px;">{{ attr.label }}</span>
                            <v-slider v-model="draftAttributes[attr.key]" :min="1" :max="5" :step="1" hide-details dense thumb-label style="flex: 1;" />
                            <span class="text-body-2 font-weight-medium" style="min-width: 24px;">{{ draftAttributes[attr.key] }}/5</span>
                        </div>
                        <div class="d-flex justify-end mt-2" style="gap: 8px;">
                            <v-btn small outlined color="grey" @click="editingAttributes = false">
                                <v-icon left small>mdi-close</v-icon> Cancel
                            </v-btn>
                            <v-btn small color="success" dark @click="saveAttributes">
                                <v-icon left small>mdi-check</v-icon> Save
                            </v-btn>
                        </div>
                    </div>
                    <div v-else class="d-flex flex-wrap mt-1 editable-value" style="gap: 10px; cursor: pointer; padding: 4px; border-radius: 4px;" @click="startEditAttributes">
                        <v-chip v-for="attr in attributeLabels" :key="attr.key" small outlined>
                            {{ attr.label }}: {{ request[attr.key] || 3 }}/5
                        </v-chip>
                        <v-icon x-small class="edit-icon ml-1 align-self-center" color="grey lighten-1">mdi-pencil</v-icon>
                    </div>
                </div>
            </v-card-text>
        </v-card>

        <!-- Candidates Section -->
        <v-card outlined class="mb-4">
            <v-card-title class="d-flex align-center justify-space-between">
                <span class="text-subtitle-1 font-weight-bold">Candidates ({{ candidates.length }})</span>
                <div class="d-flex align-center" style="gap: 8px;">
                    <v-menu v-if="candidates.length > 0" offset-y left>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn small outlined v-bind="attrs" v-on="on">
                                <v-icon left small>mdi-download</v-icon> Export
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-item @click="exportToXlsx">
                                <v-list-item-icon class="mr-2"><v-icon small>mdi-file-excel</v-icon></v-list-item-icon>
                                <v-list-item-title class="text-body-2">Download as Excel (.xlsx)</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="exportForSheets">
                                <v-list-item-icon class="mr-2"><v-icon small>mdi-google-spreadsheet</v-icon></v-list-item-icon>
                                <v-list-item-title class="text-body-2">Copy for Google Sheets</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                    <v-btn small color="primary" @click="addCandidateDialog = true">
                        <v-icon left small>mdi-plus</v-icon> Add
                    </v-btn>
                </div>
            </v-card-title>

            <!-- Candidate Filters -->
            <v-card-text class="pt-0 pb-2">
                <div class="d-flex flex-wrap align-center" style="gap: 12px;">
                    <v-text-field
                        v-model="candidateSearch"
                        placeholder="Search name or email..."
                        prepend-inner-icon="mdi-magnify"
                        outlined dense hide-details clearable
                        style="max-width: 350px;"
                    />
                    <v-select
                        v-model="candidateStatusFilter"
                        :items="candidateStatusFilterOptions"
                        label="All Statuses"
                        outlined dense hide-details
                        style="max-width: 170px;"
                    />
                    <v-select
                        v-model="candidateSourceFilter"
                        :items="candidateSourceFilterOptions"
                        label="All Sources"
                        outlined dense hide-details
                        style="max-width: 160px;"
                    />
                    <v-select
                        v-model="candidateRatingFilter"
                        :items="ratingFilterOptions"
                        label="Any Rating"
                        outlined dense hide-details
                        style="max-width: 140px;"
                    />
                </div>
            </v-card-text>

            <!-- Bulk Actions Bar -->
            <v-card-text v-if="selectedCandidateIds.length > 0" class="pt-0 pb-2">
                <v-sheet color="red lighten-5" class="d-flex align-center pa-2 rounded" style="gap: 12px;">
                    <v-icon small color="error">mdi-checkbox-marked</v-icon>
                    <span class="text-body-2 font-weight-medium">{{ selectedCandidateIds.length }} candidate{{ selectedCandidateIds.length !== 1 ? 's' : '' }} selected</span>
                    <v-spacer />
                    <v-btn small text @click="selectedCandidateIds = []">Clear</v-btn>
                    <v-btn small color="error" dark @click="confirmBulkDelete = true">
                        <v-icon left small>mdi-delete-sweep</v-icon> Delete Selected
                    </v-btn>
                </v-sheet>
            </v-card-text>

            <!-- Candidates Table -->
            <v-simple-table v-if="filteredCandidates.length > 0">
                <template v-slot:default>
                    <thead>
                        <tr>
                            <th style="width: 40px;">
                                <v-simple-checkbox
                                    :value="allFilteredSelected"
                                    :indeterminate="someFilteredSelected && !allFilteredSelected"
                                    @input="toggleSelectAll"
                                    color="primary"
                                />
                            </th>
                            <th class="cursor-pointer" @click="toggleSort('name')">
                                Name
                                <v-icon x-small v-if="sortField === 'name'">{{ sortAsc ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                            </th>
                            <th>Email</th>
                            <th>Source</th>
                            <th class="cursor-pointer" @click="toggleSort('status')">
                                Status
                                <v-icon x-small v-if="sortField === 'status'">{{ sortAsc ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                            </th>
                            <th class="cursor-pointer" @click="toggleSort('rating')">
                                Rating
                                <v-icon x-small v-if="sortField === 'rating'">{{ sortAsc ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                            </th>
                            <th>Notes</th>
                            <th>Dean</th>
                            <th>AI</th>
                            <th class="cursor-pointer" @click="toggleSort('created_at')">
                                Date Added
                                <v-icon x-small v-if="sortField === 'created_at'">{{ sortAsc ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                            </th>
                            <th style="width: 40px;"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="c in filteredCandidates" :key="c.id" style="cursor: pointer;" @click="openEditCandidate(c)">
                            <td @click.stop>
                                <v-simple-checkbox
                                    :value="selectedCandidateIds.includes(c.id)"
                                    @input="toggleCandidateSelection(c.id)"
                                    color="primary"
                                />
                            </td>
                            <td class="font-weight-medium">{{ c.name }}</td>
                            <td class="text-caption grey--text">{{ c.email || '—' }}</td>
                            <td class="text-caption">{{ c.source || '—' }}</td>
                            <td @click.stop>
                                <v-select
                                    :value="c.status"
                                    :items="candidateStatuses"
                                    dense
                                    hide-details
                                    style="max-width: 180px; font-size: 12px;"
                                    @change="updateCandidateStatus(c, $event)"
                                />
                            </td>
                            <td @click.stop>
                                <div class="d-flex">
                                    <v-icon v-for="r in 5" :key="r" x-small
                                        :color="r <= (c.rating || 0) ? 'amber' : 'grey lighten-2'"
                                        @click.stop="updateCandidateRating(c, r)"
                                        style="cursor: pointer;"
                                    >mdi-star</v-icon>
                                </div>
                            </td>
                            <td class="text-caption" style="max-width: 200px;">
                                <span class="text-truncate d-block">{{ c.notes ? c.notes.substring(0, 40) + (c.notes.length > 40 ? '...' : '') : '—' }}</span>
                            </td>
                            <td @click.stop>
                                <v-chip v-if="c.dean_approval_status === 'pending'" x-small color="amber" dark>Pending</v-chip>
                                <div v-else-if="c.dean_approval_status === 'modify_offer'">
                                    <div class="d-flex align-center" style="gap: 4px;">
                                        <v-btn icon x-small color="warning" @click="openDeanSubmitDialog(c)" title="Re-submit — Dean requested offer modification">
                                            <v-icon small>mdi-send</v-icon>
                                        </v-btn>
                                        <v-chip x-small color="deep-orange" dark>Modify</v-chip>
                                    </div>
                                    <div v-if="c.dean_approval_notes" class="text-caption deep-orange--text mt-1" style="font-size: 10px !important; line-height: 1.3; max-width: 160px;">
                                        <v-icon x-small color="deep-orange" class="mr-1" style="vertical-align: text-top;">mdi-message-text-outline</v-icon>{{ c.dean_approval_notes }}
                                    </div>
                                </div>
                                <div v-else-if="c.dean_approval_status === 'approved'">
                                    <v-icon small color="success">mdi-check-circle</v-icon>
                                    <div v-if="c.dean_approval_notes" class="text-caption grey--text mt-1" style="font-size: 10px !important; line-height: 1.3; max-width: 160px;">
                                        {{ c.dean_approval_notes }}
                                    </div>
                                </div>
                                <div v-else-if="c.dean_approval_status === 'rejected'">
                                    <v-icon small color="error">mdi-close-circle</v-icon>
                                    <div v-if="c.dean_approval_notes" class="text-caption error--text mt-1" style="font-size: 10px !important; line-height: 1.3; max-width: 160px;">
                                        {{ c.dean_approval_notes }}
                                    </div>
                                </div>
                                <v-btn v-else-if="!c.submitted_for_dean_approval" icon x-small @click="openDeanSubmitDialog(c)" title="Submit for Dean's Approval">
                                    <v-icon small>mdi-send</v-icon>
                                </v-btn>
                            </td>
                            <td>
                                <div class="d-flex align-center" style="gap: 4px;">
                                    <v-btn v-if="c.cv_file_url && !c.ai_cv_summary" icon x-small
                                        :loading="analyzingCvId === c.id"
                                        @click.stop="triggerCvAnalysis(c)"
                                        title="Analyze CV with AI"
                                        color="primary"
                                    >
                                        <v-icon small>mdi-robot-outline</v-icon>
                                    </v-btn>
                                    <v-icon v-else-if="c.ai_cv_summary" small color="success" title="AI analysis available">mdi-robot-happy-outline</v-icon>
                                    <span v-else class="text-caption grey--text">—</span>
                                </div>
                            </td>
                            <td class="text-caption grey--text text-no-wrap">{{ c.created_at ? $date(c.created_at).format('MMM D, YYYY') : '—' }}</td>
                            <td>
                                <v-btn icon x-small color="error" @click.stop="candidateToDelete = c; confirmDeleteCandidate = true" title="Delete candidate">
                                    <v-icon small>mdi-delete-outline</v-icon>
                                </v-btn>
                            </td>
                        </tr>
                    </tbody>
                </template>
            </v-simple-table>
            <v-card-text v-else class="text-center py-8">
                <v-icon size="48" color="grey lighten-1">mdi-account-search</v-icon>
                <p class="mt-4 grey--text">No candidates found.</p>
            </v-card-text>
        </v-card>

        <!-- ==================== EDIT CANDIDATE DIALOG ==================== -->
        <v-dialog v-model="editCandidateDialog" max-width="560" persistent>
            <v-card>
                <v-card-title class="d-flex align-center">
                    <v-icon color="primary" class="mr-2">mdi-account-edit</v-icon>
                    <span>Edit Candidate</span>
                    <v-spacer />
                    <v-btn icon small @click="editCandidateDialog = false"><v-icon small>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text class="pt-2">
                    <v-row dense>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="editCandidateForm.name" label="Full Name" outlined dense hide-details class="mb-3" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="editCandidateForm.email" label="Email" outlined dense hide-details class="mb-3" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="editCandidateForm.phone" label="Phone" outlined dense hide-details class="mb-3" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="editCandidateForm.salary_expectation" label="Salary Expectation" outlined dense hide-details class="mb-3" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select v-model="editCandidateForm.source" label="Source" :items="candidateSources" outlined dense hide-details class="mb-3" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select v-model="editCandidateForm.status" label="Pipeline Status" :items="candidateStatuses" outlined dense hide-details class="mb-3" />
                        </v-col>
                        <v-col cols="12">
                            <div class="text-caption grey--text font-weight-bold mb-1">Rating</div>
                            <div class="d-flex align-center" style="gap: 2px;">
                                <v-btn v-for="r in 5" :key="r" icon small @click="editCandidateForm.rating = r">
                                    <v-icon :color="r <= editCandidateForm.rating ? 'amber darken-1' : 'grey lighten-2'">mdi-star</v-icon>
                                </v-btn>
                                <v-btn v-if="editCandidateForm.rating" icon x-small @click="editCandidateForm.rating = 0" class="ml-1" title="Clear rating">
                                    <v-icon x-small>mdi-close</v-icon>
                                </v-btn>
                            </div>
                        </v-col>
                        <v-col cols="12" class="mt-2">
                            <v-textarea v-model="editCandidateForm.notes" label="Notes" outlined dense rows="3" auto-grow hide-details />
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions class="px-6 pb-4">
                    <v-spacer />
                    <v-btn text @click="editCandidateDialog = false">Cancel</v-btn>
                    <v-btn color="primary" :loading="savingEditCandidate" @click="saveEditCandidate">
                        <v-icon left small>mdi-content-save</v-icon> Save Changes
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Activity Log -->
        <v-card v-if="request.activity_logs && request.activity_logs.length > 0" outlined class="mb-4">
            <v-card-title class="text-subtitle-1 font-weight-bold">Activity Log</v-card-title>
            <v-card-text class="pt-0">
                <div v-for="(log, idx) in request.activity_logs" :key="idx" class="d-flex align-start mb-2">
                    <span class="text-caption grey--text mr-3" style="min-width: 100px;">{{ formatDate(log.created_at) }}</span>
                    <span class="font-weight-medium text-body-2 mr-2"
                        :class="getActivityColor(log.type)"
                    >{{ log.type }}</span>
                    <span class="text-caption grey--text text--darken-1">— {{ log.description }}</span>
                    <span v-if="log.user" class="text-caption grey--text ml-2">({{ log.user.name }})</span>
                </div>
            </v-card-text>
        </v-card>

        <!-- Add Candidate Dialog -->
        <v-dialog v-model="addCandidateDialog" max-width="600" scrollable>
            <v-card>
                <v-card-title>
                    Add Candidate
                    <v-spacer />
                    <v-btn icon @click="addCandidateDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <!-- Tabs -->
                    <v-tabs v-model="addCandidateTab" centered class="mb-4">
                        <v-tab>Manual Entry</v-tab>
                        <v-tab><v-icon left small>mdi-file-delimited</v-icon> CSV / Excel</v-tab>
                        <v-tab><v-icon left small color="#0F9D58">mdi-google-spreadsheet</v-icon> Google Sheets</v-tab>
                    </v-tabs>

                    <v-tabs-items v-model="addCandidateTab">
                        <!-- Tab 0: Manual Entry -->
                        <v-tab-item>
                            <v-text-field v-model="newCandidate.name" label="Name *" outlined dense class="mt-2" />
                            <v-row>
                                <v-col cols="6"><v-text-field v-model="newCandidate.email" label="Email" outlined dense /></v-col>
                                <v-col cols="6"><v-text-field v-model="newCandidate.phone" label="Phone" outlined dense /></v-col>
                            </v-row>
                            <v-select v-model="newCandidate.source" :items="candidateSources" label="Source" outlined dense />
                            <v-textarea v-model="newCandidate.notes" label="Notes" outlined dense rows="3" />
                        </v-tab-item>

                        <!-- Tab 1: Import from CSV / Excel -->
                        <v-tab-item>
                            <div class="text-center pa-6" style="border: 2px dashed #e0e0e0; border-radius: 8px;">
                                <div class="d-flex justify-center mb-2" style="gap: 4px;">
                                    <v-icon size="40" color="grey lighten-1">mdi-file-delimited-outline</v-icon>
                                    <v-icon size="24" color="grey lighten-1" style="align-self: flex-end; margin-bottom: 2px;">mdi-slash-forward</v-icon>
                                    <v-icon size="40" color="green lighten-2">mdi-file-excel-outline</v-icon>
                                </div>
                                <p class="text-body-2 mb-1">Upload a <strong>CSV</strong> or <strong>Excel (.xlsx / .xls)</strong> file</p>
                                <p class="text-caption grey--text mb-3">
                                    Header row must include a <strong>Name</strong> column.<br>
                                    Optional columns: Email, Phone, Source, Notes
                                </p>
                                <div class="d-flex justify-center align-center" style="gap: 8px;">
                                    <v-btn outlined small @click="$refs.fileInput.click()">
                                        <v-icon left small>mdi-upload</v-icon> Choose File
                                    </v-btn>
                                    <v-btn outlined small @click="downloadTemplate">
                                        <v-icon left small>mdi-download</v-icon> Download Template
                                    </v-btn>
                                </div>
                                <input ref="fileInput" type="file" accept=".csv,.txt,.tsv,.xlsx,.xls" style="display: none;" @change="handleFileImport" />
                                <div v-if="importedFileName" class="mt-3">
                                    <v-chip small outlined>
                                        <v-icon left x-small>mdi-file</v-icon>{{ importedFileName }}
                                    </v-chip>
                                </div>

                                <v-alert
                                    v-if="sheetParseError"
                                    type="error"
                                    dense
                                    text
                                    class="mt-3 mb-0 text-left"
                                >
                                    {{ sheetParseError }}
                                </v-alert>

                                <v-card v-if="importHeaders.length > 0" outlined class="mt-4 pa-3 text-left">
                                    <div class="text-subtitle-2 font-weight-bold mb-2">Map file columns to candidate fields</div>
                                    <div class="text-caption grey--text mb-3">Match your uploaded columns to fields used in the candidates table.</div>
                                    <v-row dense>
                                        <v-col v-for="field in importFieldDefinitions" :key="field.key" cols="12" sm="6">
                                            <v-select
                                                v-model="importFieldMapping[field.key]"
                                                :items="importHeaderOptions"
                                                :label="field.label + (field.required ? ' *' : '')"
                                                outlined
                                                dense
                                                hide-details
                                                clearable
                                                @change="applyImportMapping()"
                                            />
                                        </v-col>
                                    </v-row>
                                </v-card>

                                <div v-if="importedCandidates.length > 0" class="mt-3">
                                    <v-chip small color="success" dark>{{ importedCandidates.length }} candidates ready to import</v-chip>
                                </div>
                                <p class="text-caption grey--text mt-3" style="font-size: 10px;">
                                    <strong>Apple Numbers:</strong> File &rarr; Export To &rarr; CSV &nbsp;|&nbsp;
                                    <strong>Excel:</strong> Upload .xlsx directly or save as CSV
                                </p>
                            </div>
                        </v-tab-item>

                        <!-- Tab 2: Google Sheets -->
                        <v-tab-item>
                            <div class="pa-2 pt-3">
                                <div class="d-flex align-start mb-4" style="gap: 10px;">
                                    <v-icon color="#0F9D58" size="32" class="mt-1">mdi-google-spreadsheet</v-icon>
                                    <div>
                                        <div class="text-subtitle-2 font-weight-bold">Import from Google Sheets</div>
                                        <div class="text-caption grey--text mt-1">
                                            Paste the URL of a publicly shared sheet below. The sheet must be shared as
                                            <strong>"Anyone with the link can view"</strong>.
                                        </div>
                                    </div>
                                </div>

                                <v-text-field
                                    v-model="googleSheetsUrl"
                                    label="Google Sheets URL"
                                    placeholder="https://docs.google.com/spreadsheets/d/..."
                                    outlined
                                    dense
                                    clearable
                                    :error-messages="googleSheetsError ? [googleSheetsError] : []"
                                    prepend-inner-icon="mdi-link"
                                    @input="googleSheetsError = ''"
                                />

                                <v-text-field
                                    v-model.number="googleSheetsTabIndex"
                                    label="Sheet number"
                                    type="number"
                                    min="1"
                                    outlined
                                    dense
                                    hide-details
                                    class="mb-4"
                                    style="max-width: 160px;"
                                    hint="1 = first sheet"
                                />

                                <v-btn
                                    color="#0F9D58"
                                    dark
                                    small
                                    :loading="fetchingGSheets"
                                    :disabled="!googleSheetsUrl"
                                    @click="fetchGoogleSheets"
                                >
                                    <v-icon left small>mdi-cloud-download-outline</v-icon>
                                    Fetch & Preview
                                </v-btn>

                                <v-alert type="info" dense text border="left" class="mt-4 mb-0" style="font-size: 12px;">
                                    <strong>How to share:</strong> In Google Sheets &rarr;
                                    <em>Share</em> &rarr; Change to <em>"Anyone with the link"</em> &rarr; Viewer &rarr; Copy link.
                                </v-alert>

                                <v-alert
                                    v-if="sheetParseError && addCandidateTab === 2"
                                    type="error"
                                    dense
                                    text
                                    class="mt-3 mb-0"
                                >
                                    {{ sheetParseError }}
                                </v-alert>

                                <v-card v-if="importHeaders.length > 0 && addCandidateTab === 2" outlined class="mt-4 pa-3">
                                    <div class="text-subtitle-2 font-weight-bold mb-1">Map columns to candidate fields</div>
                                    <div class="text-caption grey--text mb-3">
                                        Detected <strong>{{ importRows.length }}</strong> data rows. Match each field to the correct column.
                                    </div>
                                    <v-row dense>
                                        <v-col v-for="field in importFieldDefinitions" :key="field.key" cols="12" sm="6">
                                            <v-select
                                                v-model="importFieldMapping[field.key]"
                                                :items="importHeaderOptions"
                                                :label="field.label + (field.required ? ' *' : '')"
                                                outlined
                                                dense
                                                hide-details
                                                clearable
                                                @change="applyImportMapping()"
                                            />
                                        </v-col>
                                    </v-row>
                                </v-card>

                                <div v-if="importedCandidates.length > 0 && addCandidateTab === 2" class="mt-3">
                                    <v-chip small color="success" dark>
                                        {{ importedCandidates.length }} candidates ready to import
                                    </v-chip>
                                </div>
                            </div>
                        </v-tab-item>
                    </v-tabs-items>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="addCandidateDialog = false">Cancel</v-btn>
                    <v-btn v-if="addCandidateTab === 0" color="primary" :disabled="!newCandidate.name" :loading="savingCandidate" @click="addCandidate">
                        Add Candidate
                    </v-btn>
                    <v-btn v-else color="primary" :disabled="importedCandidates.length === 0" :loading="savingCandidate" @click="importCandidates">
                        Import {{ importedCandidates.length }} Candidate{{ importedCandidates.length !== 1 ? 's' : '' }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete Job Request Confirmation Dialog -->
        <v-dialog v-model="confirmDelete" max-width="400">
            <v-card>
                <v-card-title class="error white--text">Delete Job Request</v-card-title>
                <v-card-text class="pt-4">
                    Are you sure you want to delete this job request? This action cannot be undone.
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="confirmDelete = false">Cancel</v-btn>
                    <v-btn color="error" @click="$emit('delete', request.id); confirmDelete = false">Delete</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete Single Candidate Confirmation Dialog -->
        <v-dialog v-model="confirmDeleteCandidate" max-width="420">
            <v-card>
                <v-card-title class="error white--text">
                    <v-icon left color="white">mdi-delete</v-icon> Delete Candidate
                </v-card-title>
                <v-card-text class="pt-4">
                    Are you sure you want to remove <strong>{{ candidateToDelete ? candidateToDelete.name : '' }}</strong> from this job request? This action cannot be undone.
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="confirmDeleteCandidate = false">Cancel</v-btn>
                    <v-btn color="error" :loading="deletingCandidate" @click="deleteCandidate">
                        <v-icon left small>mdi-delete</v-icon> Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Bulk Delete Candidates Confirmation Dialog -->
        <v-dialog v-model="confirmBulkDelete" max-width="450">
            <v-card>
                <v-card-title class="error white--text">
                    <v-icon left color="white">mdi-delete-sweep</v-icon> Delete {{ selectedCandidateIds.length }} Candidate{{ selectedCandidateIds.length !== 1 ? 's' : '' }}
                </v-card-title>
                <v-card-text class="pt-4">
                    Are you sure you want to remove <strong>{{ selectedCandidateIds.length }}</strong> selected candidate{{ selectedCandidateIds.length !== 1 ? 's' : '' }} from this job request? This action cannot be undone.
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="confirmBulkDelete = false">Cancel</v-btn>
                    <v-btn color="error" :loading="deletingCandidate" @click="bulkDeleteCandidates">
                        <v-icon left small>mdi-delete-sweep</v-icon> Delete All
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Submit for Dean's Approval Dialog -->
        <v-dialog v-model="deanSubmitDialog" max-width="600" scrollable persistent>
            <v-card>
                <v-card-title class="d-flex align-center">
                    <span v-if="deanSubmitPhase === 'form'">{{ isResubmission ? 'Resubmit' : 'Submit' }} {{ deanSubmitCandidate ? deanSubmitCandidate.name : '' }} for Dean's Approval</span>
                    <span v-else-if="deanSubmitPhase === 'analyzing'">Analyzing CV...</span>
                    <span v-else>Review AI Analysis</span>
                    <v-spacer />
                    <v-btn icon @click="deanSubmitDialog = false" :disabled="deanSubmitPhase === 'analyzing'"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>

                <!-- ═══ PHASE 1: Form ═══ -->
                <template v-if="deanSubmitPhase === 'form'">
                    <v-card-text class="pt-2">
                        <!-- Dean's Modification Notes Banner -->
                        <v-alert v-if="isResubmission && deanSubmitCandidate && deanSubmitCandidate.dean_approval_notes"
                            type="warning" dense text border="left" class="mb-4"
                        >
                            <div class="font-weight-bold text-body-2">Dean's Modification Request</div>
                            <div class="text-body-2 mt-1">{{ deanSubmitCandidate.dean_approval_notes }}</div>
                        </v-alert>

                        <div v-if="deanSubmitCandidate" class="mb-4">
                            <div class="text-overline grey--text">Candidate</div>
                            <div class="font-weight-bold text-body-1">{{ deanSubmitCandidate.name }}</div>
                            <div class="text-caption grey--text">{{ deanSubmitCandidate.email }}<span v-if="deanSubmitCandidate.source"> &middot; {{ deanSubmitCandidate.source }}</span></div>
                        </div>

                        <div class="text-subtitle-2 mb-1">Salary Expectation *</div>
                        <v-row dense>
                            <v-col cols="4">
                                <v-select
                                    v-model="deanSubmitForm.salary_currency"
                                    :items="['AED', '$ USD', '€ EUR', '£ GBP', 'PHP']"
                                    outlined dense
                                />
                            </v-col>
                            <v-col cols="8">
                                <v-text-field
                                    v-model="deanSubmitForm.salary_amount"
                                    placeholder="e.g. 25,000 - 30,000/month"
                                    outlined dense
                                />
                            </v-col>
                        </v-row>
                        <p v-if="isResubmission && deanSubmitCandidate && deanSubmitCandidate.salary_expectation" class="text-caption grey--text mt-n4 mb-3">
                            Previous: {{ deanSubmitCandidate.salary_expectation }}
                        </p>

                        <div class="text-subtitle-2 mb-1">CV / Resume {{ isResubmission ? '(replace or keep current)' : '*' }}</div>
                        <div class="d-flex align-center mb-4" style="gap: 8px;">
                            <v-btn outlined small @click="$refs.cvFileInput.click()">
                                <v-icon left small>mdi-upload</v-icon> Choose file
                            </v-btn>
                            <span v-if="deanSubmitForm.cv_file" class="text-caption">{{ deanSubmitForm.cv_file.name }}</span>
                            <span v-else-if="deanSubmitCandidate && deanSubmitCandidate.cv_file_url" class="text-caption green--text">
                                <v-icon x-small color="green">mdi-check</v-icon> Existing CV on file
                            </span>
                            <span v-else class="text-caption grey--text">No file selected</span>
                        </div>
                        <input ref="cvFileInput" type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" style="display: none;" @change="onCvFileSelected" />

                        <div class="text-subtitle-2 mb-1">Job Offer Document {{ isResubmission ? '(replace or keep current)' : '(optional)' }}</div>
                        <div class="d-flex align-center mb-4" style="gap: 8px;">
                            <v-btn outlined small @click="$refs.offerFileInput.click()">
                                <v-icon left small>mdi-upload</v-icon> Choose file
                            </v-btn>
                            <span v-if="deanSubmitForm.offer_file" class="text-caption">{{ deanSubmitForm.offer_file.name }}</span>
                            <span v-else-if="deanSubmitCandidate && deanSubmitCandidate.job_offer_url" class="text-caption green--text">
                                <v-icon x-small color="green">mdi-check</v-icon> Existing offer on file
                            </span>
                            <span v-else class="text-caption grey--text">No file selected</span>
                        </div>
                        <input ref="offerFileInput" type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" style="display: none;" @change="onOfferFileSelected" />

                        <div class="text-subtitle-2 mb-1">Recording Link <span class="text-caption grey--text">(optional)</span></div>
                        <v-text-field
                            v-model="deanSubmitForm.recording_link"
                            placeholder="https://..."
                            outlined dense hide-details class="mb-4"
                            prepend-inner-icon="mdi-video-outline"
                        />

                        <v-textarea
                            v-model="deanSubmitForm.dean_notes"
                            :label="isResubmission ? 'Response Notes for Dean' : 'Additional Notes for Dean'"
                            :placeholder="isResubmission ? 'Explain what was changed based on Dean\'s feedback...' : 'Any context or recommendations...'"
                            outlined dense rows="4"
                        />

                        <p class="text-caption grey--text mt-1">
                            <v-icon x-small color="primary" class="mr-1">mdi-robot-outline</v-icon>
                            AI CV analysis will run first, then you can review before submitting.
                        </p>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer />
                        <v-btn text @click="deanSubmitDialog = false">Cancel</v-btn>
                        <v-btn color="primary" :loading="submittingDean"
                            :disabled="!deanSubmitForm.salary_amount || (!deanSubmitForm.cv_file && !(deanSubmitCandidate && deanSubmitCandidate.cv_file_url))"
                            @click="saveAndAnalyze"
                        >
                            <v-icon left>mdi-robot-outline</v-icon> Save &amp; Analyze CV
                        </v-btn>
                    </v-card-actions>
                </template>

                <!-- ═══ PHASE 2: Analyzing ═══ -->
                <template v-else-if="deanSubmitPhase === 'analyzing'">
                    <v-card-text class="text-center py-8">
                        <v-progress-circular indeterminate color="primary" size="48" width="3" class="mb-4" />
                        <div class="text-body-1 font-weight-bold mb-1">Running AI CV Analysis</div>
                        <div class="text-body-2 grey--text">Extracting content and analyzing with AI. This may take a moment...</div>
                        <div v-if="deanSubmitCandidate" class="mt-4">
                            <v-chip small outlined>
                                <v-icon left x-small>mdi-account</v-icon>
                                {{ deanSubmitCandidate.name }}
                            </v-chip>
                        </div>
                    </v-card-text>
                </template>

                <!-- ═══ PHASE 3: Review AI & Submit ═══ -->
                <template v-else-if="deanSubmitPhase === 'done'">
                    <v-card-text class="pt-3">
                        <!-- AI Analysis Result -->
                        <v-card outlined class="pa-3 mb-4" style="background: #FAFAFA; border-radius: 8px;">
                            <div class="d-flex align-center mb-2">
                                <v-icon color="primary" small class="mr-1">mdi-robot-outline</v-icon>
                                <span class="font-weight-bold text-body-2">AI CV Analysis</span>
                                <v-spacer />
                                <v-icon v-if="deanSubmitAiResult" small color="success">mdi-check-circle</v-icon>
                                <v-icon v-else small color="warning">mdi-alert-circle-outline</v-icon>
                            </div>
                            <div v-if="deanSubmitAiResult"
                                class="text-body-2"
                                style="white-space: pre-line; line-height: 1.55; max-height: 300px; overflow-y: auto;"
                            >{{ deanSubmitAiResult }}</div>
                            <div v-else class="text-caption grey--text">
                                AI analysis could not be generated. You can still submit for approval.
                            </div>
                        </v-card>

                        <v-alert type="info" dense text border="left" class="mb-0">
                            <div class="text-body-2">Review the AI analysis above, then submit for Dean's approval.</div>
                        </v-alert>
                    </v-card-text>
                    <v-card-actions>
                        <v-btn text @click="deanSubmitDialog = false">Cancel</v-btn>
                        <v-spacer />
                        <v-btn color="primary" :loading="submittingDean" @click="confirmSubmitForDean">
                            <v-icon left>mdi-send</v-icon> {{ isResubmission ? 'Resubmit for Approval' : 'Submit for Dean\'s Approval' }}
                        </v-btn>
                    </v-card-actions>
                </template>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import EditableField from './EditableField.vue';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { extractPdfContent, extractImageContent } from '@/services/pdfExtractor';

export default {
    name: 'JobRequestDetail',
    components: { EditableField },
    props: {
        request: { type: Object, required: true },
        users: { type: Array, default: () => [] },
        statuses: { type: Array, default: () => [] },
    },
    data() {
        return {
            // Candidates
            candidateSearch: '',
            candidateStatusFilter: 'all',
            candidateSourceFilter: 'all',
            candidateRatingFilter: 'all',
            sortField: 'name',
            sortAsc: true,
            // Attribute editing
            editingAttributes: false,
            draftAttributes: {
                attribute_technical_skills: 3,
                attribute_cultural_fit: 3,
                attribute_problem_solving: 3,
                attribute_communication: 3,
                attribute_industry_experience: 3,
            },
            // Add Candidate
            addCandidateDialog: false,
            addCandidateTab: 0,
            savingCandidate: false,
            newCandidate: { name: '', email: '', phone: '', source: 'Job Board', notes: '' },
            importedCandidates: [],
            importedFileName: '',
            importHeaders: [],
            importRows: [],
            googleSheetsUrl: '',
            googleSheetsTabIndex: 1,
            googleSheetsError: '',
            fetchingGSheets: false,
            importFieldDefinitions: [
                { key: 'name', label: 'Name', required: true },
                { key: 'email', label: 'Email', required: false },
                { key: 'phone', label: 'Phone', required: false },
                { key: 'source', label: 'Source', required: false },
                { key: 'notes', label: 'Notes', required: false },
            ],
            importFieldMapping: {
                name: null,
                email: null,
                phone: null,
                source: null,
                notes: null,
            },
            sheetParseError: '',
            confirmDelete: false,
            // Edit candidate
            editCandidateDialog: false,
            editCandidateForm: {},
            savingEditCandidate: false,
            // Candidate deletion
            confirmDeleteCandidate: false,
            confirmBulkDelete: false,
            candidateToDelete: null,
            deletingCandidate: false,
            selectedCandidateIds: [],
            // Dean Approval Dialog
            deanSubmitDialog: false,
            deanSubmitCandidate: null,
            submittingDean: false,
            analyzingCvId: null,
            deanSubmitPhase: 'form',
            deanSubmitAiResult: '',
            deanSubmitForm: {
                salary_currency: 'AED',
                salary_amount: '',
                cv_file: null,
                offer_file: null,
                recording_link: '',
                dean_notes: '',
            },
            // Reference data
            jobStatuses: [
                'Draft', 'Submitted', 'Job Posted', 'Applicants Under Review',
                'Shortlist Created', 'Interviews In Progress', 'Final Decision Pending',
                'Approved', 'Offer Accepted', 'Onboarding In Progress', 'Closed'
            ],
            candidateStatuses: [
                'Applied', 'Shortlisted', 'Interview 1 Done', 'Interview 2 Done',
                "For Dean's Approval", 'Offer Modification Requested', 'Offer Extended', 'Hired', 'Rejected'
            ],
            candidateSources: ['Job Board', 'LinkedIn', 'Referral', 'Company Website', 'Recruitment Agency', 'Other'],
        };
    },
    computed: {
        isResubmission() {
            return this.deanSubmitCandidate && this.deanSubmitCandidate.dean_approval_status === 'modify_offer';
        },
        formattedSalaryExpectation() {
            const currency = this.deanSubmitForm.salary_currency || '';
            const amount = (this.deanSubmitForm.salary_amount || '').trim();
            if (!amount) return '';
            return currency ? `${currency} ${amount}` : amount;
        },
        candidates() {
            return this.request.candidates || [];
        },
        attributeLabels() {
            return [
                { key: 'attribute_technical_skills', label: 'Technical Skills' },
                { key: 'attribute_cultural_fit', label: 'Cultural Fit' },
                { key: 'attribute_problem_solving', label: 'Problem Solving' },
                { key: 'attribute_communication', label: 'Communication' },
                { key: 'attribute_industry_experience', label: 'Industry Experience' },
            ];
        },
        hasAttributePriorities() {
            return this.attributeLabels.some(a => this.request[a.key] && this.request[a.key] !== 3);
        },
        candidateStatusFilterOptions() {
            return [
                { text: 'All Statuses', value: 'all' },
                ...this.candidateStatuses.map(s => ({ text: s, value: s })),
            ];
        },
        candidateSourceFilterOptions() {
            const sources = [...new Set(this.candidates.map(c => c.source).filter(Boolean))];
            return [
                { text: 'All Sources', value: 'all' },
                ...sources.map(s => ({ text: s, value: s })),
            ];
        },
        ratingFilterOptions() {
            return [
                { text: 'Any Rating', value: 'all' },
                { text: '5 Stars', value: 5 },
                { text: '4+ Stars', value: 4 },
                { text: '3+ Stars', value: 3 },
                { text: '2+ Stars', value: 2 },
                { text: '1+ Stars', value: 1 },
            ];
        },
        allFilteredSelected() {
            return this.filteredCandidates.length > 0 && this.filteredCandidates.every(c => this.selectedCandidateIds.includes(c.id));
        },
        someFilteredSelected() {
            return this.filteredCandidates.some(c => this.selectedCandidateIds.includes(c.id));
        },
        filteredCandidates() {
            let result = [...this.candidates];
            if (this.candidateSearch) {
                const s = this.candidateSearch.toLowerCase();
                result = result.filter(c =>
                    c.name?.toLowerCase().includes(s) || c.email?.toLowerCase().includes(s)
                );
            }
            if (this.candidateStatusFilter !== 'all') {
                result = result.filter(c => c.status === this.candidateStatusFilter);
            }
            if (this.candidateSourceFilter !== 'all') {
                result = result.filter(c => c.source === this.candidateSourceFilter);
            }
            if (this.candidateRatingFilter !== 'all') {
                result = result.filter(c => (c.rating || 0) >= this.candidateRatingFilter);
            }
            // Sort
            result.sort((a, b) => {
                let va = a[this.sortField] || '';
                let vb = b[this.sortField] || '';
                if (typeof va === 'string') va = va.toLowerCase();
                if (typeof vb === 'string') vb = vb.toLowerCase();
                if (va < vb) return this.sortAsc ? -1 : 1;
                if (va > vb) return this.sortAsc ? 1 : -1;
                return 0;
            });
            return result;
        },
        importHeaderOptions() {
            return this.importHeaders.map((header, index) => ({
                text: header || `Column ${index + 1}`,
                value: index,
            }));
        },
    },
    watch: {
        addCandidateDialog(value) {
            if (!value) {
                this.resetImportState();
            }
        },
    },
    methods: {
        storageUrl(path) {
            if (!path) return '';
            if (path.startsWith('http://') || path.startsWith('https://')) return path;
            const base = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '');
            const clean = path.replace(/^\/+/, '');
            return `${base}/api/recruitment/files/${clean}`;
        },
        formatDate(date) {
            if (!date) return '';
            return this.$date(date).format(`MMM D, ${this.$tz.time}`);
        },
        isStepCompleted(status, idx) {
            const currentIdx = this.jobStatuses.indexOf(this.request.status);
            return idx <= currentIdx;
        },
        getStepColor(status, idx) {
            const currentIdx = this.jobStatuses.indexOf(this.request.status);
            if (idx < currentIdx) return '#4CAF50';
            if (idx === currentIdx) return '#1976D2';
            return '#E0E0E0';
        },
        getActivityColor(type) {
            if (type.includes('created')) return 'success--text';
            if (type.includes('changed')) return 'info--text';
            if (type.includes('added') || type.includes('Candidate')) return 'primary--text';
            if (type.includes('scheduled') || type.includes('Interview')) return 'purple--text';
            if (type.includes('Shortlist')) return 'deep-purple--text';
            return '';
        },
        toggleSelectAll() {
            if (this.allFilteredSelected) {
                // Deselect all filtered
                const filteredIds = this.filteredCandidates.map(c => c.id);
                this.selectedCandidateIds = this.selectedCandidateIds.filter(id => !filteredIds.includes(id));
            } else {
                // Select all filtered
                const filteredIds = this.filteredCandidates.map(c => c.id);
                const combined = new Set([...this.selectedCandidateIds, ...filteredIds]);
                this.selectedCandidateIds = [...combined];
            }
        },
        toggleCandidateSelection(id) {
            const idx = this.selectedCandidateIds.indexOf(id);
            if (idx >= 0) {
                this.selectedCandidateIds.splice(idx, 1);
            } else {
                this.selectedCandidateIds.push(id);
            }
        },
        async deleteCandidate() {
            if (!this.candidateToDelete) return;
            this.deletingCandidate = true;
            try {
                const response = await this.$axios.delete(`api/recruitment/candidates/${this.candidateToDelete.id}`);
                if (response.data.response) {
                    this.$toast.success(`${this.candidateToDelete.name} removed`);
                    this.selectedCandidateIds = this.selectedCandidateIds.filter(id => id !== this.candidateToDelete.id);
                    this.confirmDeleteCandidate = false;
                    this.candidateToDelete = null;
                    this.$emit('refresh');
                } else {
                    this.$toast.error(response.data.message || 'Failed to delete candidate');
                }
            } catch (error) {
                console.error('Error deleting candidate:', error);
                this.$toast.error('Failed to delete candidate');
            } finally {
                this.deletingCandidate = false;
            }
        },
        async bulkDeleteCandidates() {
            if (this.selectedCandidateIds.length === 0) return;
            this.deletingCandidate = true;
            try {
                const response = await this.$axios.post(`api/recruitment/candidates/bulk-delete`, {
                    candidate_ids: this.selectedCandidateIds,
                });
                if (response.data.response) {
                    this.$toast.success(`${this.selectedCandidateIds.length} candidate${this.selectedCandidateIds.length !== 1 ? 's' : ''} removed`);
                    this.selectedCandidateIds = [];
                    this.confirmBulkDelete = false;
                    this.$emit('refresh');
                } else {
                    this.$toast.error(response.data.message || 'Failed to delete candidates');
                }
            } catch (error) {
                console.error('Error bulk deleting candidates:', error);
                this.$toast.error('Failed to delete candidates');
            } finally {
                this.deletingCandidate = false;
            }
        },
        toggleSort(field) {
            if (this.sortField === field) {
                this.sortAsc = !this.sortAsc;
            } else {
                this.sortField = field;
                this.sortAsc = true;
            }
        },
        updateField(field, value) {
            this.$emit('update', { ...this.request, [field]: value });
        },
        startEditAttributes() {
            this.attributeLabels.forEach(attr => {
                this.draftAttributes[attr.key] = this.request[attr.key] || 3;
            });
            this.editingAttributes = true;
        },
        saveAttributes() {
            const updates = { ...this.request };
            this.attributeLabels.forEach(attr => {
                updates[attr.key] = this.draftAttributes[attr.key];
            });
            this.$emit('update', updates);
            this.editingAttributes = false;
        },
        async addCandidate() {
            if (!this.newCandidate.name) return;
            this.savingCandidate = true;
            try {
                const response = await this.$axios.post(`api/recruitment/job-requests/${this.request.id}/candidates`, this.newCandidate);
                if (response.data.response) {
                    this.$toast.success('Candidate added');
                    this.addCandidateDialog = false;
                    this.newCandidate = { name: '', email: '', phone: '', source: 'Job Board', notes: '' };
                    this.$emit('refresh');
                } else {
                    this.$toast.error(response.data.message || 'Failed to add candidate');
                }
            } catch (error) {
                console.error('Error adding candidate:', error);
                this.$toast.error('Failed to add candidate');
            } finally {
                this.savingCandidate = false;
            }
        },
        async importCandidates() {
            this.savingCandidate = true;
            try {
                // Filter out candidates that already exist in this job request (by email or name)
                const existing = this.request.candidates || [];
                const existingEmails = new Set(existing.map(c => (c.email || '').toLowerCase()).filter(Boolean));
                const existingNames  = new Set(existing.map(c => (c.name  || '').toLowerCase()).filter(Boolean));

                const fresh = this.importedCandidates.filter(c => {
                    if (c.email && existingEmails.has(c.email.toLowerCase())) return false;
                    if (existingNames.has(c.name.toLowerCase())) return false;
                    return true;
                });

                const skipped = this.importedCandidates.length - fresh.length;

                if (fresh.length === 0) {
                    const msg = skipped > 0
                        ? `All ${skipped} candidate${skipped > 1 ? 's' : ''} already exist in this request — nothing imported.`
                        : 'No candidates to import.';
                    this.$toast.warning(msg);
                    this.savingCandidate = false;
                    return;
                }

                const response = await this.$axios.post(`api/recruitment/job-requests/${this.request.id}/candidates/bulk`, {
                    candidates: fresh,
                });
                if (response.data.response) {
                    const skipNote = skipped > 0 ? `, ${skipped} duplicate${skipped > 1 ? 's' : ''} skipped` : '';
                    this.$toast.success(`${fresh.length} candidate${fresh.length > 1 ? 's' : ''} imported${skipNote}`);
                    this.addCandidateDialog = false;
                    this.resetImportState();
                    this.$emit('refresh');
                }
            } catch (error) {
                console.error('Error importing candidates:', error);
                this.$toast.error('Failed to import candidates');
            } finally {
                this.savingCandidate = false;
            }
        },
        downloadTemplate() {
            const header = 'Name,Email,Phone,Source,Notes';
            const example = 'Jane Doe,jane@example.com,0912345678,LinkedIn,Great communication skills';
            const blob = new Blob([header + '\n' + example], { type: 'text/csv' });
            saveAs(blob, 'candidates_template.csv');
        },
        resetImportState() {
            this.importedCandidates = [];
            this.importedFileName = '';
            this.importHeaders = [];
            this.importRows = [];
            this.googleSheetsUrl = '';
            this.googleSheetsTabIndex = 1;
            this.googleSheetsError = '';
            this.fetchingGSheets = false;
            this.importFieldMapping = {
                name: null,
                email: null,
                phone: null,
                source: null,
                notes: null,
            };
            this.sheetParseError = '';
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },
        parseCsvText(text) {
            if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
            const lines = text.split(/\r?\n/).filter(l => l.trim());
            if (lines.length < 2) {
                this.sheetParseError = 'Sheet must have a header row and at least one data row.';
                return;
            }
            const firstLine = lines[0];
            const delimiter = firstLine.includes('\t') ? '\t' : firstLine.includes(';') ? ';' : ',';
            const parseLine = (line) => {
                const result = [];
                let current = '';
                let inQuotes = false;
                for (let i = 0; i < line.length; i++) {
                    const ch = line[i];
                    if (ch === '"') {
                        inQuotes = !inQuotes;
                    } else if (ch === delimiter && !inQuotes) {
                        result.push(current.trim());
                        current = '';
                    } else {
                        current += ch;
                    }
                }
                result.push(current.trim());
                return result;
            };
            this.prepareImportRows(lines.map(parseLine));
        },
        async fetchGoogleSheets() {
            if (!this.googleSheetsUrl) return;
            this.googleSheetsError = '';
            this.sheetParseError = '';
            this.importedCandidates = [];
            this.importHeaders = [];
            this.importRows = [];

            // Extract spreadsheet ID
            const match = this.googleSheetsUrl.match(/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
            if (!match) {
                this.googleSheetsError = 'Invalid Google Sheets URL. Please paste the full URL from your browser.';
                return;
            }
            const sheetId = match[1];

            // Prefer gid from the URL itself; fall back to tab number (user enters 1-based, gid is 0-based)
            const gidMatch = this.googleSheetsUrl.match(/[?&#]gid=(\d+)/);
            const gid = gidMatch ? gidMatch[1] : String(Math.max(0, (parseInt(this.googleSheetsTabIndex) || 1) - 1));

            const exportUrl = `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv&gid=${gid}`;

            this.fetchingGSheets = true;
            try {
                const response = await fetch(exportUrl, { redirect: 'follow' });

                // A redirect to accounts.google.com means the sheet is private
                if (!response.ok || response.url.includes('accounts.google.com')) {
                    this.googleSheetsError = 'Sheet is not publicly accessible. Share it as "Anyone with the link can view" and try again.';
                    return;
                }

                const text = await response.text();

                // Received an HTML login page instead of CSV data
                if (text.trim().startsWith('<!')) {
                    this.googleSheetsError = 'Sheet is not publicly accessible. Share it as "Anyone with the link can view" and try again.';
                    return;
                }

                this.parseCsvText(text);
                if (!this.sheetParseError && this.importedCandidates.length > 0) {
                    this.$toast.success(`${this.importedCandidates.length} candidates fetched from Google Sheets`);
                }
            } catch (err) {
                console.error('Google Sheets fetch error:', err);
                this.googleSheetsError = 'Failed to fetch sheet. Ensure the sheet is shared publicly and try again.';
            } finally {
                this.fetchingGSheets = false;
            }
        },
        normalizeImportHeader(value) {
            return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        },
        autoMapImportFields(headers) {
            const aliases = {
                name: ['name', 'fullname', 'candidate', 'candidatename'],
                email: ['email', 'emailaddress', 'mail'],
                phone: ['phone', 'phonenumber', 'mobile', 'mobilenumber', 'contact', 'contactnumber'],
                source: ['source', 'candidatesource', 'channel', 'platform'],
                notes: ['notes', 'note', 'remarks', 'comment', 'comments'],
            };

            const normalizedHeaders = headers.map(h => this.normalizeImportHeader(h));
            const mapping = {
                name: null,
                email: null,
                phone: null,
                source: null,
                notes: null,
            };

            Object.keys(mapping).forEach(field => {
                const idx = normalizedHeaders.findIndex(h => aliases[field].includes(h));
                mapping[field] = idx >= 0 ? idx : null;
            });

            return mapping;
        },
        prepareImportRows(rows) {
            if (!rows || rows.length < 2) {
                this.sheetParseError = 'File must have a header row and at least one data row.';
                this.importedCandidates = [];
                return;
            }

            const headers = rows[0].map(h => String(h || '').trim());
            this.importHeaders = headers;
            this.importRows = rows.slice(1);
            this.importFieldMapping = this.autoMapImportFields(headers);

            if (this.importFieldMapping.name === null) {
                this.sheetParseError = 'Please map the Name field to continue.';
                this.importedCandidates = [];
                return;
            }

            this.applyImportMapping(true);
        },
        applyImportMapping(showToast = false) {
            const nameIndex = this.importFieldMapping.name;
            if (nameIndex === null || nameIndex === undefined) {
                this.sheetParseError = 'Please map the Name field to continue.';
                this.importedCandidates = [];
                return;
            }

            this.sheetParseError = '';

            const mapped = this.importRows.map(cols => {
                const read = (index) => {
                    if (index === null || index === undefined) return '';
                    return String(cols[index] || '').trim();
                };

                const sourceValue = read(this.importFieldMapping.source);

                return {
                    name: read(this.importFieldMapping.name),
                    email: read(this.importFieldMapping.email),
                    phone: read(this.importFieldMapping.phone),
                    source: sourceValue || 'Import',
                    notes: read(this.importFieldMapping.notes),
                };
            }).filter(candidate => candidate.name);

            // Deduplicate within the file itself — key on email (if present) otherwise lowercase name
            const seenKeys = new Set();
            const deduped = mapped.filter(c => {
                const key = c.email ? c.email.toLowerCase() : c.name.toLowerCase();
                if (seenKeys.has(key)) return false;
                seenKeys.add(key);
                return true;
            });

            const inFileDupes = mapped.length - deduped.length;
            this.importedCandidates = deduped;

            if (deduped.length === 0) {
                this.sheetParseError = 'No valid candidates found after applying mapping.';
                if (showToast) this.$toast.error('No valid candidates found in file');
            } else if (showToast) {
                const dupeNote = inFileDupes > 0 ? ` (${inFileDupes} duplicate row${inFileDupes > 1 ? 's' : ''} removed)` : '';
                this.$toast.success(`${deduped.length} candidates parsed${dupeNote}`);
            }
        },
        handleFileImport(event) {
            const file = event.target.files[0];
            if (!file) return;

            this.importedFileName = file.name;
            this.importedCandidates = [];
            this.sheetParseError = '';

            const ext = file.name.split('.').pop().toLowerCase();

            if (ext === 'numbers') {
                this.$toast.error('Apple Numbers files are not supported. Please export as CSV first (File → Export To → CSV).');
                this.$refs.fileInput.value = '';
                this.importedFileName = '';
                return;
            }

            if (!['csv', 'tsv', 'txt', 'xlsx', 'xls'].includes(ext)) {
                this.$toast.error('Unsupported file format. Please upload a .csv or .xlsx file.');
                this.$refs.fileInput.value = '';
                this.importedFileName = '';
                return;
            }

            const reader = new FileReader();

            if (['xlsx', 'xls'].includes(ext)) {
                // Parse Excel file using SheetJS
                reader.onload = (e) => {
                    try {
                        const workbook = XLSX.read(e.target.result, { type: 'binary' });
                        const sheet = workbook.Sheets[workbook.SheetNames[0]];
                        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
                        this.prepareImportRows(rows);
                    } catch (err) {
                        console.error('Excel parse error:', err);
                        this.$toast.error('Failed to read Excel file. Please ensure it is a valid .xlsx or .xls file.');
                    }
                };
                reader.readAsBinaryString(file);
            } else {
                // Parse CSV / TSV / TXT as plain text
                reader.onload = (e) => {
                    this.parseCsvText(e.target.result);
                };
                reader.readAsText(file);
            }
        },
        async updateCandidateStatus(candidate, newStatus) {
            // When moving to Dean's Approval, open the submission dialog instead
            if (newStatus === "For Dean's Approval" && (!candidate.submitted_for_dean_approval || candidate.dean_approval_status === 'modify_offer')) {
                this.openDeanSubmitDialog(candidate);
                return;
            }
            try {
                const payload = { status: newStatus };
                await this.$axios.post(`api/recruitment/candidates/${candidate.id}`, payload);
                candidate.status = newStatus;
                this.$toast.success('Status updated');
                this.$emit('refresh');
            } catch (error) {
                console.error('Error updating candidate status:', error);
                this.$toast.error('Failed to update status');
            }
        },
        async updateCandidateRating(candidate, rating) {
            try {
                await this.$axios.post(`api/recruitment/candidates/${candidate.id}`, { rating });
                candidate.rating = rating;
            } catch (error) {
                console.error('Error updating rating:', error);
            }
        },
        async submitForDeanApproval(candidate) {
            try {
                await this.$axios.post(`api/recruitment/candidates/${candidate.id}`, {
                    submitted_for_dean_approval: true,
                    dean_approval_status: 'pending',
                    status: "For Dean's Approval",
                });
                candidate.submitted_for_dean_approval = true;
                candidate.dean_approval_status = 'pending';
                this.$toast.success('Submitted for Dean\'s approval');
                this.$emit('refresh');
            } catch (error) {
                console.error('Error submitting for approval:', error);
                this.$toast.error('Failed to submit for approval');
            }
        },
        // ─── Dean Approval Dialog Methods ───
        openDeanSubmitDialog(candidate) {
            this.deanSubmitCandidate = candidate;
            this.deanSubmitPhase = 'form';
            this.deanSubmitAiResult = '';
            // Parse existing salary_expectation into currency + amount
            const parsed = this.parseSalaryExpectation(candidate.salary_expectation);
            this.deanSubmitForm = {
                salary_currency: parsed.currency,
                salary_amount: parsed.amount,
                cv_file: null,
                offer_file: null,
                recording_link: candidate.recording_link || '',
                dean_notes: '',
            };
            this.deanSubmitDialog = true;
        },
        parseSalaryExpectation(value) {
            if (!value) return { currency: 'AED', amount: '' };
            const currencies = ['$ USD', '€ EUR', '£ GBP', 'AED', 'PHP'];
            const str = String(value).trim();
            for (const c of currencies) {
                if (str.startsWith(c + ' ') || str.startsWith(c)) {
                    return { currency: c, amount: str.slice(c.length).trim() };
                }
            }
            return { currency: 'AED', amount: str };
        },
        onCvFileSelected(event) {
            const file = event.target.files[0];
            if (file) this.deanSubmitForm.cv_file = file;
            event.target.value = '';
        },
        onOfferFileSelected(event) {
            const file = event.target.files[0];
            if (file) this.deanSubmitForm.offer_file = file;
            event.target.value = '';
        },
        async saveAndAnalyze() {
            if (!this.deanSubmitCandidate) return;
            this.submittingDean = true;
            try {
                // Step 1: Save candidate data + files (without submitting for dean approval yet)
                const formData = new FormData();
                const salaryExpectation = this.formattedSalaryExpectation;
                formData.append('salary_expectation', salaryExpectation);
                formData.append('save_only', '1');
                if (this.deanSubmitForm.dean_notes) {
                    formData.append('dean_approval_notes', this.deanSubmitForm.dean_notes);
                }
                if (this.deanSubmitForm.cv_file) {
                    formData.append('cv_file', this.deanSubmitForm.cv_file);
                }
                if (this.deanSubmitForm.offer_file) {
                    formData.append('offer_file', this.deanSubmitForm.offer_file);
                }
                if (this.deanSubmitForm.recording_link) {
                    formData.append('recording_link', this.deanSubmitForm.recording_link);
                }

                const response = await this.$axios.post(
                    `api/recruitment/candidates/${this.deanSubmitCandidate.id}/submit-dean`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );

                if (response.data.response) {
                    // Update local candidate data
                    this.deanSubmitCandidate.salary_expectation = this.formattedSalaryExpectation;
                    if (response.data.data && response.data.data.cv_file_url) {
                        this.deanSubmitCandidate.cv_file_url = response.data.data.cv_file_url;
                    }

                    // Check if AI analysis already exists
                    const existingAnalysis = this.deanSubmitCandidate.ai_cv_summary
                        || (this.request.candidates || []).find(c => c.id === this.deanSubmitCandidate.id)?.ai_cv_summary;

                    if (existingAnalysis) {
                        // Skip AI analysis — use existing
                        this.deanSubmitAiResult = existingAnalysis;
                        this.deanSubmitPhase = 'done';
                    } else {
                        // No existing analysis — run AI
                        this.deanSubmitPhase = 'analyzing';
                        this.submittingDean = false;

                        const analysisCandidate = { id: this.deanSubmitCandidate.id, cv_file_url: this.deanSubmitCandidate.cv_file_url };
                        if (this.deanSubmitForm.cv_file) {
                            analysisCandidate._cvFile = this.deanSubmitForm.cv_file;
                        }
                        const aiResult = await this.triggerCvAnalysis(analysisCandidate, true);
                        this.deanSubmitAiResult = aiResult || '';
                        this.deanSubmitPhase = 'done';
                    }
                } else {
                    this.$toast.error(response.data.message || 'Failed to save');
                }
            } catch (error) {
                console.error('Error saving candidate data:', error);
                this.$toast.error('Failed to save candidate data');
            } finally {
                this.submittingDean = false;
            }
        },
        async confirmSubmitForDean() {
            if (!this.deanSubmitCandidate) return;
            this.submittingDean = true;
            try {
                // Now actually submit for dean approval (files already saved in saveAndAnalyze)
                const formData = new FormData();
                formData.append('salary_expectation', this.deanSubmitCandidate.salary_expectation || this.formattedSalaryExpectation);
                // No save_only flag → sets submitted_for_dean_approval + dean_approval_status
                const response = await this.$axios.post(
                    `api/recruitment/candidates/${this.deanSubmitCandidate.id}/submit-dean`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );

                if (response.data.response) {
                    this.deanSubmitCandidate.submitted_for_dean_approval = true;
                    this.deanSubmitCandidate.dean_approval_status = 'pending';
                    this.$toast.success('Submitted for Dean\'s approval');
                    this.deanSubmitDialog = false;
                    this.$emit('refresh');
                } else {
                    this.$toast.error(response.data.message || 'Failed to submit');
                }
            } catch (error) {
                console.error('Error submitting for dean approval:', error);
                this.$toast.error('Failed to submit for approval');
            } finally {
                this.submittingDean = false;
            }
        },
        // ─── AI CV Analysis ───
        async triggerCvAnalysis(candidate, silent = false) {
            if (!candidate || !candidate.id) return;
            this.analyzingCvId = candidate.id;
            if (!silent) this.$toast.info('Extracting CV content for AI analysis...');

            try {
                // Determine the CV source — either the File just uploaded or the existing URL
                let cvSource = candidate._cvFile || null; // File object attached during dean submit
                let cvUrl = null;
                if (!cvSource && candidate.cv_file_url) {
                    cvUrl = this.storageUrl(candidate.cv_file_url); // Full URL string
                }
                if (!cvSource && !cvUrl) {
                    if (!silent) this.$toast.error('No CV file available for this candidate');
                    console.warn('triggerCvAnalysis: No CV source available');
                    return null;
                }

                // If we have a URL (existing file), fetch it with auth first
                if (!cvSource && cvUrl) {
                    const token = localStorage.getItem('vuex')
                        ? JSON.parse(localStorage.getItem('vuex'))?.auth?.token
                        : null;
                    const fileResp = await axios.get(cvUrl, {
                        responseType: 'arraybuffer',
                        headers: token ? { Authorization: `Bearer ${token}` } : {},
                    });
                    const ext = cvUrl.split('?')[0].split('.').pop().toLowerCase();
                    if (['png', 'jpg', 'jpeg', 'webp'].includes(ext)) {
                        cvSource = new Blob([fileResp.data], { type: fileResp.headers['content-type'] || 'image/png' });
                    } else {
                        // PDF — pass ArrayBuffer directly
                        cvSource = fileResp.data;
                    }
                }

                // Extract text + page images on the frontend
                let extracted;
                const ext = cvSource instanceof ArrayBuffer
                    ? 'pdf'
                    : typeof cvSource === 'string'
                        ? cvSource.split('?')[0].split('.').pop().toLowerCase()
                        : (cvSource.name || '').split('.').pop().toLowerCase() || 'pdf';

                if (ext === 'pdf') {
                    extracted = await extractPdfContent(cvSource, {
                        maxPages: 10,
                        imageScale: 1.5,
                        imageQuality: 0.75,
                    });
                } else if (['png', 'jpg', 'jpeg', 'webp'].includes(ext)) {
                    // Image-only CV — just encode it
                    if (cvSource instanceof Blob || cvSource instanceof File) {
                        extracted = await extractImageContent(cvSource);
                    } else {
                        // Should not reach here after auth fetch, but handle gracefully
                        extracted = await extractImageContent(cvSource);
                    }
                } else {
                    if (!silent) this.$toast.error('Unsupported CV file type: ' + ext);
                    console.warn('triggerCvAnalysis: Unsupported file type:', ext);
                    return null;
                }

                if (!extracted.extractedText && !extracted.images.length) {
                    if (!silent) this.$toast.error('Could not extract any content from the CV');
                    console.warn('triggerCvAnalysis: No text or images extracted from CV');
                    return null;
                }

                if (!silent) this.$toast.info('Sending to AI for analysis...');

                // Send extracted content to backend
                const response = await this.$axios.post(
                    `api/recruitment/candidates/${candidate.id}/analyze-cv`,
                    {
                        extracted_text: extracted.extractedText,
                        images: extracted.images,
                    }
                );

                if (response.data.response) {
                    // Update the candidate in our local array
                    const local = (this.request.candidates || []).find(c => c.id === candidate.id);
                    if (local) {
                        local.ai_cv_summary = response.data.data.ai_cv_summary;
                    }
                    this.$toast.success('AI CV analysis complete');
                    this.$emit('refresh');
                    return response.data.data.ai_cv_summary || 'Analysis complete (no summary returned).';
                } else {
                    if (!silent) this.$toast.error(response.data.message || 'Analysis failed');
                    return null;
                }
            } catch (error) {
                console.error('CV analysis error:', error);
                console.warn('triggerCvAnalysis failed:', error.message || error);
                if (!silent) this.$toast.error('CV analysis failed: ' + (error.message || 'Unknown error'));
                return null;
            } finally {
                this.analyzingCvId = null;
            }
        },
        // ─── Export Methods ───
        getExportRows() {
            return this.filteredCandidates.map(c => ({
                Name: c.name || '',
                Email: c.email || '',
                Phone: c.phone || '',
                Source: c.source || '',
                Status: c.status || '',
                Rating: c.rating || '',
                Notes: c.notes || '',
                'Salary Expectation': c.salary_expectation || '',
                'Dean Approval': c.dean_approval_status || '',
            }));
        },
        exportToXlsx() {
            const rows = this.getExportRows();
            if (rows.length === 0) {
                this.$toast.error('No candidates to export');
                return;
            }
            const ws = XLSX.utils.json_to_sheet(rows);
            // Auto-width columns
            const colWidths = Object.keys(rows[0]).map(key => {
                const maxLen = Math.max(key.length, ...rows.map(r => String(r[key]).length));
                return { wch: Math.min(maxLen + 2, 40) };
            });
            ws['!cols'] = colWidths;
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Candidates');
            const fileName = `${(this.request.job_title || 'Candidates').replace(/[^a-zA-Z0-9 ]/g, '')}_Candidates.xlsx`;
            const wbOut = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            saveAs(new Blob([wbOut], { type: 'application/octet-stream' }), fileName);
            this.$toast.success(`Exported ${rows.length} candidates to Excel`);
        },
        exportForSheets() {
            const rows = this.getExportRows();
            if (rows.length === 0) {
                this.$toast.error('No candidates to export');
                return;
            }
            const headers = Object.keys(rows[0]);
            const tsv = [
                headers.join('\t'),
                ...rows.map(r => headers.map(h => String(r[h]).replace(/\t/g, ' ')).join('\t'))
            ].join('\n');
            navigator.clipboard.writeText(tsv).then(() => {
                this.$toast.success(`${rows.length} candidates copied — paste into Google Sheets (Ctrl+V)`);
            }).catch(() => {
                this.$toast.error('Failed to copy to clipboard');
            });
        },
        // ─── Edit Candidate ───
        openEditCandidate(candidate) {
            this.editCandidateForm = {
                id: candidate.id,
                name: candidate.name || '',
                email: candidate.email || '',
                phone: candidate.phone || '',
                source: candidate.source || 'Job Board',
                status: candidate.status || 'Applied',
                rating: candidate.rating || 0,
                notes: candidate.notes || '',
                salary_expectation: candidate.salary_expectation || '',
            };
            this.editCandidateDialog = true;
        },
        async saveEditCandidate() {
            if (!this.editCandidateForm.id) return;
            this.savingEditCandidate = true;
            try {
                const response = await this.$axios.post(
                    `api/recruitment/candidates/${this.editCandidateForm.id}`,
                    this.editCandidateForm
                );
                if (response.data.response) {
                    this.$toast.success('Candidate updated');
                    // Update candidate in the request's candidates array
                    const updated = response.data.data;
                    const id = this.editCandidateForm.id;
                    const idx = this.request.candidates ? this.request.candidates.findIndex(c => c.id === id) : -1;
                    if (idx !== -1) {
                        this.$set(this.request.candidates, idx, { ...this.request.candidates[idx], ...updated });
                    }
                    this.$emit('candidate-updated', updated);
                    this.editCandidateDialog = false;
                } else {
                    this.$toast.error(response.data.message || 'Failed to update candidate');
                }
            } catch (error) {
                console.error('Error updating candidate:', error);
                this.$toast.error('Failed to update candidate');
            } finally {
                this.savingEditCandidate = false;
            }
        },
    },
};
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}
.editable-value {
    transition: background-color 0.2s;
}
.editable-value:hover {
    background-color: rgba(0, 0, 0, 0.04);
}
.edit-icon {
    opacity: 0;
    transition: opacity 0.2s;
}
.editable-value:hover .edit-icon {
    opacity: 1;
}
</style>
